import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { PageTabs } from '../../components/PageTabs';
import { founderListApi, suspendUserApi, restoreUserApi, updateUserDetailApi } from '../../api/usersApi';
import { dateFormat } from '../../utils/formattedDate';
import SingleSuspendPopup from '../../components/SingleSuspendPopup';
import MultipleSuspendPopup from '../../components/MultipleSuspendPopup';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import HeaderInfo from '../../components/HeaderInfo';
import ActiveFounders from './ActiveFounders';
import NotCompletedFounders from './NotCompletedFounders';
import AddNewFounder from '../../components/Founders/AddNewFounder';
import SuspendedFounders from './SuspendedFounders';
import Notification from '../../components/Notification';

import '../../assets/css/Founders.css';
import '../../assets/css/Popup.css';
import { getUserCounts, getUserDetailApi } from '../../api/usersApi';
import EditUser from '../../components/EditSlider/EditUser';

const Founders = () => {
  const location = useLocation();
  const userEditRef = useRef();
  const addFounderRef = useRef(null);
  const suspendMultipleFounderRef = useRef();
  const suspendFoundersRef = useRef();
  const debounceRef = useRef(null);
  const reportEditRef = useRef();

  const [menuActive, setMenuActive] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isEditFounder, setEditFounder] = useState(false);
  const [selectedFounder, setSelectFounder] = useState({});
  const [founderCount, setFounderCount] = useState([]);
  const [showAddNewFounderPopup, setShowAddNewFounderPopup] = useState(false);
  const [activePage, setActivePage] = useState(
    `Active Founders (${founderCount[1]})`,
  );
  const [ProfileTabs, setProfileTabs] = useState([]);
  const [founderList, setFounderList] = useState([]);
  const [totalPage, setTotalPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSuspendLoading, setIsSuspendLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFounderSuspendPopup, setShowSuspendPopup] = useState(false);
  const [showFounderMultipleSuspendPopup, setMultipleSuspendPopup] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleSelect, setToggleSelect] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [reason, setReason] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [personalEmail, setPersonalEmail] = useState(selectedFounder?.email || '')
  const [registedEmail, setRegistedEmail] = useState('')
  const [isEditReport, setEditReport] = useState(false);
  const [filterFounder, setFilterFounder] = useState({
    investmentTerm: [],
    startupStage: [],
    industry: [],
    university: [],
    teamCount: [],
    registedDate: '',
    suspendedDate: ''
  });

  const currentStatus = useMemo(() => {
    const path = location.pathname.toLowerCase();
    if (path.includes('/notcompleted')) return 'notCompleted';
    if (path.includes('/suspended')) return 'suspended';
    return 'active';
  }, [location.pathname]);

  useEffect(() => {
    const statusLabelMap = {
      active: `Active Founders (${founderCount[1] || 0})`,
      notCompleted: `Not Completed (${founderCount[2] || 0})`,
      suspended: `Suspended (${founderCount[3] || 0})`
    };
    setActivePage(statusLabelMap[currentStatus]);

    setProfileTabs([
      { name: statusLabelMap['active'], href: '/founders/active' },
      { name: statusLabelMap['notCompleted'], href: '/founders/notcompleted' },
      { name: statusLabelMap['suspended'], href: '/founders/suspended' }
    ]);

    setSelectedRows([]);
    setIsAllSelected(false);
  }, [currentStatus, founderCount]);

  useEffect(() => {
    fetchFoundersCount();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    setIsLoadingData(true);
    if (debounceRef?.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchFounders();
    }, 1500);

    return () => clearTimeout(debounceRef.current);
  }, [currentPage, location?.pathname, filterFounder, searchQuery]);

  useEffect(() => {
    setCurrentPage(1)
  }, [filterFounder, searchQuery])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isEditReport && reportEditRef?.current) {
        if (!reportEditRef.current.contains(event.target)) {
          setEditReport(false);
          return;
        }
      }
      if (!isEditReport && userEditRef?.current) {
        if (!userEditRef.current.contains(event.target)) {
          setEditFounder(false);
        }
      }
      if (
        suspendFoundersRef?.current &&
        !suspendFoundersRef?.current.contains(event.target)
      ) {
        setShowSuspendPopup(false);
      }
      if (
        suspendMultipleFounderRef?.current &&
        !suspendMultipleFounderRef?.current.contains(event.target)
      ) {
        setMultipleSuspendPopup(false);
      }
      if (
        addFounderRef?.current &&
        !addFounderRef?.current.contains(event.target)
      ) {
        setShowAddNewFounderPopup(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditReport, reportEditRef]);

  const fetchFoundersCount = async () => {
    try {
      const res = await getUserCounts({ type: 1 });
      const countsByStatus = {
        1: 0,
        2: 0,
        3: 0,
      };
      res.forEach(item => {
        if (countsByStatus.hasOwnProperty(item?.status)) {
          countsByStatus[item?.status] = item?.count;
        }
      });
      setFounderCount(countsByStatus);
    } catch (error) {
      console.log(error);
    }
  };

  const handleEditClick = async (row) => {
    try {
      setEditFounder(true);
      setSelectFounder(row);
    } catch (error) {
      console.error('Failed to fetch user detail on edit:', error);
    }
  };

  const founderApi = async () => {
    const status = currentStatus;
    const data = await founderListApi({
      status,
      page: currentPage || 1,
      university: filterFounder?.university?.map((item) => item?.toString()),
      industry: filterFounder?.industry?.map((item) => item?.toString()),
      startupStage: filterFounder?.startupStage?.map((item) => item?.toString()),
      investmentTerm: filterFounder?.investmentTerm?.map((item) => item?.toString()),
      teamCount: filterFounder?.teamCount?.map((item) => item?.toString()),
      registeredStart: (filterFounder?.registedDate?.from !== '' && filterFounder?.registedDate?.from !== undefined && filterFounder?.registedDate?.from) ? dateFormat(filterFounder?.registedDate?.from) : undefined,
      registeredEnd: (filterFounder?.registedDate?.to !== '' && filterFounder?.registedDate?.to !== undefined && filterFounder?.registedDate?.to) ? dateFormat(filterFounder?.registedDate?.to) : undefined,
      suspendedStart: status !== 'notCompleted' ? ((filterFounder?.suspendedDate?.from !== '' && filterFounder?.suspendedDate?.from !== undefined && filterFounder?.suspendedDate?.from) ? dateFormat(filterFounder?.suspendedDate?.from) : undefined) : undefined,
      suspendedEnd: status !== 'notCompleted' ? ((filterFounder?.suspendedDate?.to !== '' && filterFounder?.suspendedDate?.to !== undefined && filterFounder?.suspendedDate?.to) ? dateFormat(filterFounder?.suspendedDate?.to) : undefined) : undefined,
      search: searchQuery?.toLowerCase() || undefined,
    }, setIsLoadingData);
    return data
  }

  const fetchFounders = async () => {
    setIsLoading(false);
    try {
      setIsLoading(true)
      setIsLoadingData(true)
      const data = await founderApi();
      if (data?.list?.length <= 0) {
        setIsLoadingData(false)
      }
      setFounderList(data?.list);
      setTotalPage(data?.totalPage)
      setIsLoading(false)
    } catch (error) {
      console.log(error);
      setIsLoading(false)
      setIsLoadingData(false)
    }
  };

  const suspendUser = async (reason, userId, showFounderMultipleSuspendPopup = false) => {
    try {
      await suspendUserApi({
        reason,
        userIds: showFounderMultipleSuspendPopup ? userId : [userId],
      });
      return true;
    } catch (error) {
      console.error('API error:', error.response?.data || error.message);
      return false;
    }
  };

  const restoreUser = async (reason, userId, showFounderMultipleSuspendPopup = false) => {
    try {
      await restoreUserApi({
        reason,
        userIds: showFounderMultipleSuspendPopup ? userId : [userId],
      });
      return true;
    } catch (error) {
      console.error('API error:', error.response?.data || error.message);
      return false;
    }
  }

  const updateData = async (changedPersonalEmail, changedRegisterEmail) => {
    try {
      await updateUserDetailApi({
        userId: selectedFounder?.userId,
        data: {
          email: changedPersonalEmail,
          registeredEmail: changedRegisterEmail
        },
      });
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  const handleUpdateUser = async (changedPersonalEmail, changedRegisterEmail) => {
    setIsSaveLoading(false)
    setShowNotification(false);
    try {
      setIsSaveLoading(true)
      const success = await updateData(changedPersonalEmail, changedRegisterEmail);
      if (success) {
        const data = await founderApi()
        if (data) {
          setIsLoadingData(false)
          setFounderList(data?.list);
          setShowNotification(true);
          setNotificationMessage('Your Change have been successfully saved.');
          setTimeout(() => {
            setShowNotification(false);
          }, 8000);
          setIsSaveLoading(false)
          setEditFounder(false)
        }
        return true
      } else {
        setIsSaveLoading(false)
      }
    } catch (error) {
      console.log(error);
      setIsSaveLoading(false)
    }
  }

  const handleSuspendUser = async (reason, profileData) => {
    setIsSuspendLoading(false)
    setShowNotification(false);
    try {
      setIsSuspendLoading(true)
      const success = await suspendUser(reason, profileData?.userId);
      if (success) {
        const data = await founderApi()
        await fetchFoundersCount()
        if (data) {
          setIsLoadingData(false)
          setFounderList(data?.list);
          setTotalPage(data?.totalPage)
          setShowNotification(true);
          setNotificationMessage(`${profileData?.name || ""} was suspended from Ivycorn.` || "");
          setTimeout(() => {
            setShowNotification(false);
          }, 8000);
          setIsSuspendLoading(false)
          setEditFounder(false)
          if (data?.list?.length === 0) {
            setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
          }
        }
        return true
      } else {
        setIsSuspendLoading(false)
      }
    } catch (error) {
      console.log(error);
      setIsSuspendLoading(false)
    }
  }

  const handleRestoreUser = async (reason, profileData) => {
    console.log({ profileData })
    setIsSuspendLoading(false)
    setShowNotification(false);
    try {
      setIsSuspendLoading(true)
      const success = await restoreUser(reason, profileData?.userId);
      if (success) {
        const data = await founderApi()
        await fetchFoundersCount()
        if (data) {
          setIsLoadingData(false)
          setFounderList(data?.list);
          setTotalPage(data?.totalPage)
          setShowNotification(true);
          setNotificationMessage(`${profileData?.name || ""} was restored from Ivycorn.` || "");
          setTimeout(() => {
            setShowNotification(false);
          }, 8000);
          setIsSuspendLoading(false)
          setEditFounder(false)
          if (data?.list?.length === 0) {
            setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
          }
        }
        return true
      } else {
        setIsSuspendLoading(false)
      }
    } catch (error) {
      console.log(error);
      setIsSuspendLoading(false)
    }
  }

  const handleActionUser = async (reason) => {
    let status = currentStatus;
    setShowNotification(false);
    setErrorMessage('');
    let notificationText = '';
    let success = false;
    if (!reason.trim()) {
      setErrorMessage(
        status !== "suspended"
          ? "Please enter reason for suspension."
          : "Please enter reason for restore."
      );
      return;
    }

    setIsSuspendLoading(true)
    if (status === "suspended") {
      if (showFounderSuspendPopup) {
        notificationText = `${selectedFounder?.name || ""} was restored from Ivycorn.`;
      } else {
        notificationText = `${selectedRows?.length} founders were restored from Ivycorn.`
      }
      success = await restoreUser(reason, showFounderSuspendPopup ? selectedFounder?.userId : selectedRows, showFounderMultipleSuspendPopup);
    } else {
      if (showFounderSuspendPopup) {
        notificationText = `${selectedFounder?.name || ""} was suspended from Ivycorn.`;
      } else {
        notificationText = `${selectedRows?.length} founders were suspended from Ivycorn.`
      }
      success = await suspendUser(reason, showFounderSuspendPopup ? selectedFounder?.userId : selectedRows, showFounderMultipleSuspendPopup);
    }
    if (success) {
      const data = await founderApi()
      await fetchFoundersCount()
      if (data) {
        setFounderList(data?.list);
        setTotalPage(data?.totalPage)
        setShowNotification(true);
        setNotificationMessage(notificationText);
        setToggleSelect(!toggleSelect);
        setTimeout(() => {
          setShowNotification(false);
        }, 8000);
        setMultipleSuspendPopup(false)
        setShowSuspendPopup(false)
        setReason('')
        if (data?.list?.length === 0) {
          setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
        }
      }
      setIsSuspendLoading(false)
    } else {
      setIsSuspendLoading(false)
    }
  };

  const LazyImage = ({ src, alt }) => {
    const [isVisible, setIsVisible] = useState(false);
    const imgRef = useRef();

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );

      if (imgRef.current) {
        observer.observe(imgRef.current);
      }

      return () => {
        if (imgRef.current) {
          observer.unobserve(imgRef.current);
        }
      };
    }, []);

    return (
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading="lazy"
      />
    );
  };

  const founderUserList = useMemo(() => {
    return founderList
  }, [founderList])

  const founderProps = {
    setShowAddNewFounderPopup,
    setSelectFounder,
    isLoading,
    isLoadingData,
    founderList: founderUserList,
    totalPage,
    setEditFounder,
    setIsLoadingData,
    currentPage,
    setSearchQuery,
    searchQuery,
    setFilterFounder,
    filterFounder,
    setShowSuspendPopup,
    setMultipleSuspendPopup,
    selectedRows,
    setSelectedRows,
    isAllSelected,
    setIsAllSelected,
    toggleSelect,
    setErrorMessage,
    setCurrentPage,
    setReason,
    LazyImage
  };
  const getFounderComponent = (Component) => <Component {...founderProps} />;

  return (
    <div className="main-wrapper">
      <Header
        activePage="founders"
        menuActive={menuActive}
        setMenuActive={setMenuActive}
      />
      <div className="main-content">
        <div className={`content-wrapper`}>
          <HeaderInfo
            title="Founders"
            description={
              activePage?.startsWith('Active')
                ? `You have ${founderCount[1] || 0} active founders.`
                : activePage.startsWith('Not Completed')
                  ? `You have ${founderCount[2] || 0} not completed founders.`
                  : activePage.startsWith('Suspended')
                    ? `You have ${founderCount[3] || 0} suspended founders.`
                    : ''
            }
          />
          <PageTabs
            tabs={ProfileTabs}
            activePage={activePage}
            setActivePage={(tabName) => {
              setActivePage(tabName);
              setFilterFounder({
                investmentTerm: [],
                startupStage: [],
                industry: [],
                university: [],
                teamCount: [],
                registedDate: '',
                suspendedDate: ''
              })
              setSearchQuery('')
            }} />

          <div className="foundersContent">
            <Routes basename="/founders">
              <Route path="/" element={getFounderComponent(ActiveFounders)} />
              <Route path="/active" element={getFounderComponent(ActiveFounders)} />
              <Route path="/notcompleted" element={getFounderComponent(NotCompletedFounders)} />
              <Route path="/suspended" element={getFounderComponent(SuspendedFounders)} />
            </Routes>
          </div>
        </div>
        <Footer menuActive={menuActive} />
      </div>

      {showNotification && (
        <Notification
          showNotification={showNotification}
          notificationMessage={notificationMessage}
          setShowNotification={setShowNotification}
        />
      )}
      {showAddNewFounderPopup && (
        <AddNewFounder
          ref={addFounderRef}
          isOpen={showAddNewFounderPopup}
          onClose={() => setShowAddNewFounderPopup(false)}
          setShowAddNewFounderPopup={setShowAddNewFounderPopup}
          setShowNotification={setShowNotification}
          setNotificationMessage={setNotificationMessage}
          setErrorMessage={setErrorMessage}
          errorMessage={errorMessage}
        />
      )}
      <EditUser
        setEdit={setEditFounder}
        ref={userEditRef}
        selectedUser={selectedFounder}
        isEditUser={isEditFounder}
        type='founder'
        handleActionUser={handleSuspendUser}
        handleRestore={handleRestoreUser}
        handleUpdateUser={handleUpdateUser}
        isSuspendLoading={isSuspendLoading}
        setSelectUser={setSelectFounder}
        setPersonalEmail={setPersonalEmail}
        personalEmail={personalEmail}
        isSaveLoading={isSaveLoading}
        setIsSaveLoading={setIsSaveLoading}
        setRegistedEmail={setRegistedEmail}
        registedEmail={registedEmail}
        reason={reason}
        setReason={setReason}
        setShowNotification={setShowNotification}
        setNotificationMessage={setNotificationMessage}
        reportEditRef={reportEditRef}
        isEditReport={isEditReport}
        setEditReport={setEditReport}
        userApi={founderApi}
        fetchUserCount={fetchFoundersCount}
        setUserList={setFounderList}
        setTotalPage={setTotalPage}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
      />
      {
        showFounderSuspendPopup &&
        <SingleSuspendPopup
          isOpen={showFounderSuspendPopup}
          onClose={() => { setShowSuspendPopup(false) }}
          title={`Are you sure you want to ${activePage == `Suspended (${founderCount[3] || 0})` ? 'restore' : 'suspend'} ${selectedFounder?.name}?`}
          description={`${activePage === `Suspended (${founderCount[3] || 0})` ? "Restoring this founder will reinstate their access to Ivycorn, allowing them to continue using the service." : "Suspending this founder means they will be permanently suspended from using Ivycorn."}`}
          ref={suspendFoundersRef}
          status={activePage === `Suspended (${founderCount[3] || 0})` && 'suspended'}
          handleActionUser={handleActionUser}
          reason={reason}
          setReason={setReason}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          isSuspendLoading={isSuspendLoading}
        />
      }
      {
        showFounderMultipleSuspendPopup &&
        <MultipleSuspendPopup
          ref={suspendMultipleFounderRef}
          isOpen={showFounderMultipleSuspendPopup}
          onClose={() => { setMultipleSuspendPopup(false) }}
          title={`Are you sure you want to ${activePage === `Suspended (${founderCount[3] || 0})` ? 'restore' : 'suspend'} these ${selectedRows?.length} founders?`}
          description={`${activePage === `Suspended (${founderCount[3] || 0})` ? "Restoring these founders will reinstate their access to Ivycorn, allowing them to continue using the service." : "Suspending these founders means they will be permanently suspended from using Ivycorn."}`}
          handleActionUser={handleActionUser}
          reason={reason}
          setReason={setReason}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          status={activePage === `Suspended (${founderCount[3] || 0})` && 'suspended'}
          isSuspendLoading={isSuspendLoading}
        />
      }
    </div>
  );
};

export default Founders;